'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

export type Language = 'ro' | 'en' | 'fr' | 'de' | 'it' | 'hu' | 'bg' | 'sr';

interface TranslationContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (text: string) => string;
  translateAsync: (text: string) => Promise<string>;
  isTranslating: boolean;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

// Client-side cache for translations
const translationCache = new Map<string, Map<Language, string>>();

function getCachedTranslation(text: string, language: Language): string | null {
  const textCache = translationCache.get(text);
  return textCache?.get(language) || null;
}

function setCachedTranslation(text: string, language: Language, translation: string): void {
  if (!translationCache.has(text)) {
    translationCache.set(text, new Map());
  }
  translationCache.get(text)!.set(language, translation);
}

export const TranslationProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>('ro');
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    // Load saved language from localStorage
    const savedLanguage = localStorage.getItem('app-language') as Language;
    if (savedLanguage && ['ro', 'en', 'fr', 'de', 'it', 'hu', 'bg', 'sr'].includes(savedLanguage)) {
      setLanguageState(savedLanguage);
    }
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('app-language', lang);
  }, []);

  const translateAsync = useCallback(async (text: string): Promise<string> => {
    if (!text || text.trim() === '') return text;
    
    // If language is Romanian (default), return original text
    if (language === 'ro') {
      return text;
    }

    // Check cache first
    const cached = getCachedTranslation(text, language);
    if (cached) {
      return cached;
    }

    try {
      setIsTranslating(true);
      const response = await fetch('/api/translate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text,
          targetLanguage: language,
          sourceLanguage: 'ro',
        }),
      });

      if (!response.ok) {
        // Silently fail and return original text
        return text;
      }

      const data = await response.json();
      const translation = data.translation || text;

      // Cache the translation
      setCachedTranslation(text, language, translation);

      return translation;
    } catch (error) {
      // Silently handle all errors (including network errors) and return original text
      return text;
    } finally {
      setIsTranslating(false);
    }
  }, [language]);

  // Synchronous translation function (returns cached or original)
  const t = useCallback((text: string): string => {
    if (!text || text.trim() === '') return text;
    
    // If language is Romanian, return original
    if (language === 'ro') {
      return text;
    }

    // Check cache only - no automatic translation triggering
    const cached = getCachedTranslation(text, language);
    if (cached) {
      return cached;
    }

    // Return original text if not cached
    return text;
  }, [language]);

  return (
    <TranslationContext.Provider data-component-id="src/contexts/TranslationContext.tsx:117:4" data-component-path="src/contexts/TranslationContext.tsx" data-component-line="117" data-component-end-line="119" data-component-file="TranslationContext.tsx" data-component-name="TranslationContext.Provider" data-component-content="%7B%22elementName%22%3A%22TranslationContext.Provider%22%7D" value={{ language, setLanguage, t, translateAsync, isTranslating }}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};